import { useCallback, useEffect, useRef, useState, type TouchEvent } from "react";
import { Link } from "react-router-dom";
import type { Movie } from "@/types/movie";
import { typeLabel } from "@/lib/movieData";

interface HeroCarouselProps {
  items: Movie[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);
  const touchStartX = useRef<number | null>(null);

  const go = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      setActive((next + items.length) % items.length);
    },
    [items.length]
  );

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 5500);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const handleTouchStart = (event: TouchEvent) => {
    touchStartX.current = event.touches[0].clientX;
  };

  const handleTouchEnd = (event: TouchEvent) => {
    if (touchStartX.current === null) return;
    const delta = event.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(delta) > 50) {
      go(active + (delta < 0 ? 1 : -1));
    }
    touchStartX.current = null;
  };

  return (
    <section
      className="relative w-full overflow-hidden bg-foreground-950"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      aria-label="精选推荐"
    >
      <div className="relative h-[440px] md:h-[600px] w-full">
        {items.map((item, index) => (
          <div
            key={item.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === active ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
          >
            <img
              src={item.backdrop || item.poster}
              alt={`${item.title}焦点推荐图`}
              title={`${item.title} 中文字幕`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/20"></div>
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-transparent"></div>

            <div className="absolute inset-0 flex items-center">
              <div className="w-full px-4 md:px-6">
                <div className="max-w-2xl animate-fade-up">
                  <span className="inline-flex items-center gap-2 rounded-full bg-accent-500/95 px-3 py-1 text-xs font-semibold text-background-50 dark:text-foreground-950">
                    <i className="ri-fire-line"></i>
                    本周精选 · {typeLabel(item.type)}
                  </span>
                  <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
                    {item.title}
                  </h1>
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-100">
                    <span>{item.year}</span>
                    <span>{item.region}</span>
                    <span>导演 {item.director}</span>
                    <span className="font-semibold text-accent-400">
                      {item.rating.toFixed(1)} 分
                    </span>
                  </div>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base line-clamp-2">
                    {item.summary}
                  </p>
                  <div className="mt-6 flex flex-wrap gap-3">
                    <Link
                      to={`/detail/${item.id}`}
                      className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950 whitespace-nowrap"
                    >
                      <i className="ri-play-circle-line text-lg"></i>
                      查看详情
                    </Link>
                    <Link
                      to={`/category/${item.type}`}
                      className="inline-flex items-center gap-2 rounded-md border border-background-100/40 bg-background-50/10 px-5 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/20 whitespace-nowrap"
                    >
                      更多{typeLabel(item.type)}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        onClick={() => go(active - 1)}
        className="absolute left-2 md:left-5 top-1/2 hidden -translate-y-1/2 items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 sm:flex"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl"></i>
      </button>
      <button
        type="button"
        onClick={() => go(active + 1)}
        className="absolute right-2 md:right-5 top-1/2 hidden -translate-y-1/2 items-center justify-center w-9 h-9 md:w-10 md:h-10 rounded-full bg-background-50/20 text-background-50 backdrop-blur transition-colors hover:bg-background-50/40 sm:flex"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl"></i>
      </button>

      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => go(index)}
            aria-label={`切换到 ${item.title}`}
            className={`h-1.5 rounded-full transition-all ${
              index === active ? "w-7 bg-accent-500" : "w-2.5 bg-background-50/50"
            }`}
          />
        ))}
      </div>
    </section>
  );
}